import React, { useState, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useParams, Link } from "react-router-dom";
import { useDispatch } from "react-redux";
import {
  Tooltip, Box, Grid, Paper, Tab,
  Select, MenuItem,
} from "@material-ui/core";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import Tabs from "@material-ui/lab/TabList";
import TabContext from "@material-ui/lab/TabContext";
import TabPanel from "@material-ui/lab/TabPanel";
import DataTable from "@View/components/table/DataTable";
import NewLeadTransferModal from "./modals/NewLeadTransferModal";
import LeadTransferDetailsModal from "./modals/LeadTransferDetailsModal";
import LeadDetailsModal from "./modals/LeadDetailsModal";
import DetailsIcon from "@View/components/svgComponents/DetailsIcon";
import EditIcon from "@View/components/svgComponents/EditIcon";
import ViewIcon from "@View/components/svgComponents/ViewIcon";
import FinDetailsIcon from "@View/components/svgComponents/FinDetailsIcon";
import ArchiveIcon from "@View/components/svgComponents/DownloadArchiveIcon";
import { downloadFile } from "@Dao/api";
import { useTooltipStyle } from "@View/style/components/tooltipStyles";
import { useTabStyle } from "@View/style/components/tabStyle";
import { useStyles } from "@View/style/pages/detailsStyle";
import useAppTab from "@Utils/useAppTab";
import { useBackButton } from "@View/hooks/useBackButton";
import { formatDate, formatDateTime } from "@Localization/utils";
import getAPI from "@API/index";
import { getHeaderLeftComponent } from "@View/components/headers/HeaderMain";
import { setLead } from "@State/reducers/getReducers/leadReducer";
import { getCustomer } from "@State/actions/actionsGet/actionGetCustomer";
import {
  requestCloseDetailsAction,
  requestOpenDetailsAction,
} from "@State/actions/actionsGet/actionGetFinDataRequest";
import RequestDetailsPage from "@View/pages/AdminPO/request/RequestDetailsPage";
import FinDataRequestModal from "@View/pages/AdminPO/request/FinDataRequestModal";
import NewRequestPage from "@View/pages/AdminPO/request/NewRequestPage";
import ButtonAddNew from "@View/components/buttons/ButtonAddNew";
import { useSelector } from "react-redux";
import { getCustomerId, canAddAbility, getRoleSafe } from "@Security/stateHelpers";
import { getLeadRoleConfig } from "./leadRoleConfig";
import LeadTransferStatusBadge from "./components/LeadTransferStatusBadge";
import ButtonBB from "@View/components/buttons/ButtonBB";
import BankStatementsModal from "@View/pages/AdminPO/customer/customerDetails/BankStatementsModal";

const TRANSFER_STATUSES = ["InProgress", "AwaitingClient", "DocumentsRequired", "Approved", "Rejected", "CreditIssued"];

const tabs = ["Transfers", "Requests"];

const TransfersTable = ({ leadId, lead, transfers, setTransfers, roleConfig }) => {
  const { t } = useTranslation();
  const tooltipStyle = useTooltipStyle();

  const { statusEditable: statusEditableByRole, showNewTransferButton, actions: actionConfig, columns: visibleColumns } = roleConfig.transfersTable;
  const statusEditable = statusEditableByRole || getRoleSafe() === "Admin";
  const isArchived = lead?.status === "Archived";

  const [openNew, setOpenNew] = useState(false);
  const [detailsTransfer, setDetailsTransfer] = useState(null);
  const [pendingStatus, setPendingStatus] = useState(null); // { transferId, newStatus }

  const handleTransferCreated = (newTransfer) => {
    setTransfers((prev) => [...prev, newTransfer]);
  };

  const allColumns = [
    {
      field: "leadReceiverCustomerName",
      headerName: t("inputs.LeadReceiver"),
      flex: 1,
      headerAlign: "left",
      align: "left",
      renderCell: (cell) => cell.row.leadReceiverCustomerName || "—",
    },
    {
      field: "requestedAmount",
      headerName: t("inputs.RequestedAmount"),
      width: 160,
      headerAlign: "left",
      align: "left",
      renderCell: (cell) =>
        cell.row.requestedAmount != null ? cell.row.requestedAmount : "—",
    },
    {
      field: "requestedTermMonths",
      headerName: t("inputs.TermMonths"),
      width: 130,
      headerAlign: "left",
      align: "left",
      renderCell: (cell) =>
        cell.row.requestedTermMonths != null ? cell.row.requestedTermMonths : "—",
    },
    {
      field: "createdAt",
      headerName: t("inputs.Create Date"),
      width: 150,
      headerAlign: "left",
      align: "left",
      renderCell: (cell) =>
        cell.row.createdAt ? formatDate(cell.row.createdAt) : "—",
    },
    {
      field: "creditIssuedAt",
      headerName: t("inputs.CreditIssuedAt"),
      width: 160,
      headerAlign: "left",
      align: "left",
      renderCell: (cell) =>
        cell.row.creditIssuedAt ? formatDate(cell.row.creditIssuedAt) : "—",
    },
    {
      field: "bankDecisionAt",
      headerName: t("inputs.BankDecisionAt"),
      width: 160,
      headerAlign: "left",
      align: "left",
      renderCell: (cell) =>
        cell.row.bankDecisionAt ? formatDate(cell.row.bankDecisionAt) : "—",
    },
    {
      field: "status",
      headerName: t("inputs.Status"),
      width: 320,
      headerAlign: "left",
      align: "left",
      renderCell: (cell) => {
        if (statusEditable) {
          const isPending = pendingStatus?.transferId === cell.row.id;
          const selectValue = isPending ? pendingStatus.newStatus : (cell.row.status || "");
          return (
            <div style={{ display: "flex", alignItems: "center", gap: 4 }} onClick={(e) => e.stopPropagation()}>
              <Select
                value={selectValue}
                renderValue={(val) => (
                  <LeadTransferStatusBadge status={val} label={t(`LeadTransferStatus.${val}`)} />
                )}
                onChange={(e) => setPendingStatus({ transferId: cell.row.id, newStatus: e.target.value })}
                disableUnderline
                style={{ fontSize: 14 }}
              >
                {TRANSFER_STATUSES.map((s) => (
                  <MenuItem key={s} value={s}>
                    <LeadTransferStatusBadge status={s} label={t(`LeadTransferStatus.${s}`)} />
                  </MenuItem>
                ))}
              </Select>
              {isPending && (
                <>
                  <CheckIcon
                    onClick={handleConfirmStatusChange}
                    style={{ cursor: "pointer", color: "#4CAF50", fontSize: 20 }}
                  />
                  <CloseIcon
                    onClick={() => setPendingStatus(null)}
                    style={{ cursor: "pointer", color: "#F44336", fontSize: 20 }}
                  />
                </>
              )}
            </div>
          );
        }
        return cell.row.status
          ? <LeadTransferStatusBadge status={cell.row.status} label={t(`LeadTransferStatus.${cell.row.status}`)} />
          : "—";
      },
    },
    {
      field: "actions",
      headerName: " ",
      width: 140,
      headerAlign: "left",
      align: "left",
      renderCell: (cell) => (
        <div className="tableIcons">
          {actionConfig.showDetails && (
            <Tooltip title={t("buttons.Details")} classes={tooltipStyle}>
              <div
                onClick={(e) => {
                  e.preventDefault();
                  setDetailsTransfer(cell.row);
                }}
                className={tooltipStyle.element}
              >
                <DetailsIcon />
              </div>
            </Tooltip>
          )}
        </div>
      ),
    },
  ];

  const columns = allColumns.filter((col) => visibleColumns.includes(col.field));

  const handleConfirmStatusChange = () => {
    if (!pendingStatus) return;
    getAPI()
      .request({
        method: "put",
        url: `/api/lead/${leadId}/transfer/${pendingStatus.transferId}/status`,
        data: { status: pendingStatus.newStatus },
        suppressError: false,
      })
      .then(() => {
        setTransfers((prev) =>
          prev.map((tr) =>
            tr.id === pendingStatus.transferId
              ? { ...tr, status: pendingStatus.newStatus }
              : tr
          )
        );
        setPendingStatus(null);
      });
  };

  return (
    <>
      <DataTable
        rows={transfers}
        columns={columns}
        toolbarButtons={
          showNewTransferButton
            ? <ButtonAddNew onClick={() => { if (!isArchived) setOpenNew(true); }} disabled={isArchived} label={t("buttons.New Transfer")} />
            : null
        }
      />
      <NewLeadTransferModal
        leadId={leadId}
        lead={lead}
        open={openNew}
        handleClose={() => setOpenNew(false)}
        onCreated={handleTransferCreated}
      />
      <LeadTransferDetailsModal
        transfer={detailsTransfer}
        open={detailsTransfer !== null}
        handleClose={() => setDetailsTransfer(null)}
      />
    </>
  );
};

const TableComponentGlobals = { shouldRowHandleClick: true };

const RequestsTab = ({ finDataRequests }) => {
  const { t } = useTranslation();
  const tooltipStyle = useTooltipStyle();
  const dispatch = useDispatch();

  const openDetails = useSelector((s) => s.finDataRequest.general.openDetails);
  const [requestId, setRequestId] = useState(null);
  const [isFinModalOpen, setIsFinModalOpen] = useState(null);

  const requestOpenDetails = (id) => {
    setRequestId(id);
    dispatch(requestOpenDetailsAction());
  };

  const requestCloseDetails = () => {
    dispatch(requestCloseDetailsAction());
  };

  const columns = [
    {
      field: "leadName",
      headerName: t("inputs.CompanyName"),
      flex: 1,
      headerAlign: "left",
      align: "left",
      renderCell: (cell) => cell.row.leadName || "—",
    },
    {
      field: "startDate",
      headerName: t("inputs.Start Date"),
      width: 140,
      headerAlign: "left",
      align: "left",
      renderCell: (cell) => cell.row.startDate ? formatDate(cell.row.startDate) : "—",
    },
    {
      field: "endDate",
      headerName: t("inputs.End Date"),
      width: 140,
      headerAlign: "left",
      align: "left",
      renderCell: (cell) => cell.row.endDate ? formatDate(cell.row.endDate) : "—",
    },
    {
      field: "updatedAt",
      headerName: t("Last Update"),
      width: 160,
      headerAlign: "left",
      align: "left",
      renderCell: (cell) => cell.row.updatedAt ? formatDateTime(cell.row.updatedAt) : "—",
    },
    {
      field: "status",
      headerName: t("inputs.Status"),
      width: 160,
      headerAlign: "left",
      align: "left",
      renderCell: (cell) => cell.row.status ? t(cell.row.status) : "—",
    },
    {
      field: "actions",
      headerName: " ",
      width: 160,
      headerAlign: "left",
      align: "left",
      sortable: false,
      renderCell: (cell) => (
        <div className="tableIcons">
          <Tooltip title={t("buttons.finReport")} classes={tooltipStyle}>
            <div
              onClick={() => setIsFinModalOpen(cell.row)}
              className={tooltipStyle.element}
            >
              <FinDetailsIcon />
            </div>
          </Tooltip>
          <Tooltip title={t("buttons.Details")} classes={tooltipStyle}>
            <div
              onClick={() => requestOpenDetails(cell.row.id)}
              className={tooltipStyle.element}
            >
              <DetailsIcon />
            </div>
          </Tooltip>
          <Tooltip title={t("buttons.View")} classes={tooltipStyle}>
            <Link
              to={`/findatarequest/${cell.row.id}/data/`}
              className={tooltipStyle.element}
            >
              <ViewIcon />
            </Link>
          </Tooltip>
          {cell.row.hasDocuments && (
            <Tooltip title={t("Скачать оригинальные файлы")} classes={tooltipStyle}>
              <div
                onClick={(e) => {
                  TableComponentGlobals.shouldRowHandleClick = false;
                  e.preventDefault();
                  downloadFile(`/api/FinDataRequest/${cell.row.id}/documents/archive`);
                }}
                className={tooltipStyle.element}
              >
                <ArchiveIcon />
              </div>
            </Tooltip>
          )}
        </div>
      ),
    },
  ];

  return (
    <>
      <DataTable rows={finDataRequests} columns={columns} />
      <RequestDetailsPage
        requestId={requestId}
        open={openDetails}
        handleOpen={requestOpenDetails}
        handleClose={requestCloseDetails}
      />
      <FinDataRequestModal
        openRequest={isFinModalOpen}
        handleOpen={() => { }}
        handleClose={() => setIsFinModalOpen(null)}
      />
    </>
  );
};

const LeadTransfersPage = () => {
  const { leadId } = useParams();
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const classes = useStyles();
  const tabClasses = useTabStyle();

  const [customerRole, setCustomerRole] = useState(null);
  const [lead, setLeadState] = useState(null);
  const [transfers, setTransfers] = useState([]);
  const [finDataRequests, setFinDataRequests] = useState([]);
  const [openDetails, setOpenDetails] = useState(false);
  const [isBankStatementsOpen, setIsBankStatementsOpen] = useState(false);

  const [tab, setTab] = useAppTab({
    defaultTab: "Transfers",
    tabList: tabs,
  });

  const leadAsClient = useMemo(() => {
    if (!lead) return null;
    return {
      id: lead.id,
      _leadId: lead.id,
      externalId: lead.inn,
      name: lead.borrowerName,
      finDataRequests: finDataRequests || [],
    };
  }, [lead, finDataRequests]);

  useEffect(() => {
    const customerId = getCustomerId();
    if (!customerId) return;
    getAPI()
      .request({ method: "get", url: `/api/Customer/${customerId}` })
      .then((data) => {
        if (data?.customerRole) setCustomerRole(data.customerRole);
      });
  }, []);

  useEffect(() => {
    if (!leadId) return;
    getAPI()
      .request({ method: "get", url: `/api/lead/${leadId}`, suppressError: false })
      .then((response) => {
        if (response?.data) {
          setLeadState(response.data);
          setTransfers(response.data.transfers ?? []);
          setFinDataRequests(response.data.finDataRequests ?? []);
          dispatch(setLead(response.data));
          if (getCustomerId() === response.data.leadGeneratorCustomerId) {
            dispatch(getCustomer(response.data.leadGeneratorCustomerId));
          }
        }
      });
  }, [leadId]);

  const LeftComponent = () => {
    const backButtonVisible = useBackButton();
    return (
      <div
        onClick={() => setOpenDetails(true)}
        style={{
          cursor: "pointer",
          display: "flex",
          alignItems: "center",
          marginLeft: backButtonVisible ? "24px" : "",
        }}
      >
        <div
          style={{
            marginRight: "15px",
            fontWeight: 500,
            fontSize: "16px",
            lineHeight: "19px",
            color: "#1A1E24",
          }}
        >
          {lead?.borrowerName}
        </div>
        <div style={{ display: "flex" }}>
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M7.25 4.25H8.75V5.75H7.25V4.25ZM8 11.75C8.4125 11.75 8.75 11.4125 8.75 11V8C8.75 7.5875 8.4125 7.25 8 7.25C7.5875 7.25 7.25 7.5875 7.25 8V11C7.25 11.4125 7.5875 11.75 8 11.75ZM8 0.5C3.86 0.5 0.5 3.86 0.5 8C0.5 12.14 3.86 15.5 8 15.5C12.14 15.5 15.5 12.14 15.5 8C15.5 3.86 12.14 0.5 8 0.5ZM8 14C4.6925 14 2 11.3075 2 8C2 4.6925 4.6925 2 8 2C11.3075 2 14 4.6925 14 8C14 11.3075 11.3075 14 8 14Z" fill="black" />
          </svg>
        </div>
      </div>
    );
  };

  useEffect(() => {
    dispatch(getHeaderLeftComponent(LeftComponent));
  }, [lead]);

  return (
    <div className="customerDetails__page">
      <LeadDetailsModal
        lead={lead}
        open={openDetails}
        handleClose={() => setOpenDetails(false)}
      />
      {leadAsClient && (
        <BankStatementsModal
          open={isBankStatementsOpen}
          handleClose={() => setIsBankStatementsOpen(false)}
          client={leadAsClient}
        />
      )}
      <TabContext value={tab}>
        <Grid className={classes.fullTable}>
          <Paper
            style={{
              boxShadow: "none",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              flexWrap: "wrap",
              gap: "10px",
            }}
          >
            <Tabs onChange={setTab} indicatorColor="primary" textColor="primary">
              {tabs.map((tabName, index) => (
                <Tab
                  classes={tabClasses}
                  label={t(tabName)}
                  value={tabName}
                  key={index}
                />
              ))}
            </Tabs>
            {tab === "Requests" &&
              canAddAbility() &&
              leadAsClient &&
              getLeadRoleConfig(customerRole).requestsTab?.showUploadButton && (
                <ButtonBB onClick={() => setIsBankStatementsOpen(true)}>
                  {t("buttons.Upload Financial Data")}
                </ButtonBB>
              )}
          </Paper>
          <Box>
            <Paper>
              <TabPanel value="Transfers" style={{ position: "relative" }}>
                <TransfersTable
                  leadId={leadId}
                  lead={lead}
                  transfers={transfers}
                  setTransfers={setTransfers}
                  roleConfig={getLeadRoleConfig(customerRole)}
                />
              </TabPanel>
              <TabPanel value="Requests" style={{ position: "relative" }}>
                <RequestsTab finDataRequests={finDataRequests} />
              </TabPanel>
            </Paper>
          </Box>
        </Grid>
      </TabContext>
    </div>
  );
};

export default LeadTransfersPage;
